import { ColumnDef, SortingFn } from '@tanstack/react-table';
import { number } from 'prop-types';
import { BaseCell } from 'src/components/TableV2/cells';
import { ConditionalCell } from 'src/components/TableV2/cells/ConditionalCell';
import { Content } from 'src/contexts/ContentContext';
import { TableHeaderCellDiv } from 'src/core/ui/TableHeaderCellDiv';
import { useColumnUserSetting } from 'src/hooks/useColumnUserSetting';
import {
  FormatOption,
  FormatOption_Percent,
} from 'src/modals/EditTableColumns';
import { TableShimmeringDiv } from 'src/tables/Table';
import { DECIMAL_OPTION_MAPPINGS } from 'src/utils/numberFormatter';
import { sortGeneric, sortMoney } from 'src/utils/tableUtils';
import { SectionType } from 'src/utils/types/sectionType';

import { SHIMMERING_DIV_HEIGHT_SINGLE_SALE } from '../../SalesTable/SalesTable.type';
import { percentRangeFilter } from '../filters/filterFunctions';

const COST_COLUMN_MIN_SIZE = 50;
const COST_COLUMN_MAX_SIZE = 125;

interface PercentColumnOptions<TData> {
  /** Column ID */
  id: string;
  /** Content ID for the header label */
  headerContentId: string;
  /** Function to extract number (as decimal, e.g., 0.15 for 15%) from data */
  accessor: (data: TData) => number | null | undefined;
  /** Support color thresholds from table meta */
  withThresholds?: boolean;
  /** Include footer with aggregated totals */
  withFooter?: boolean;
  /** Default number of decimal places (default: 2) */
  defaultDecimalPlaces?: number;
  /** Custom sorting function */
  sortingFn?: SortingFn<TData>;
  /** Function to determine if cell should be rendered (return null if false) */
  shouldRender?: (data: TData) => boolean;
  /** Function to determine shimmer height based on data */
  getShimmerHeight?: (data: TData) => string | number;
  /** Function to determine if data is still loading */
  isLoading?: (data: TData) => boolean;
  /** Column size */
  size?: number;
  /** Section type for precision settings */
  sectionType?: SectionType;
  /** Whether sorting is enabled for this column (default: true) */
  enableSorting?: boolean;
}

/**
 * Factory function to create percent column definitions for sales tables.
 * Uses ConditionalCell with percentage formatting and optional color thresholds.
 *
 * @example
 * createPercentColumn({
 *   id: SalesTableColumnId.Margin,
 *   headerContentId: SALE_TABLE_COLUMN_ID_TO_CONTENT_ID[SalesTableColumnId.Margin],
 *   accessor: (data) => data?.sale?.margin,
 *   withThresholds: true,
 *   withFooter: true,
 * })
 */
export const createPercentColumn = <TData,>(
  options: PercentColumnOptions<TData>
): ColumnDef<TData> => {
  const {
    id,
    headerContentId,
    accessor,
    withThresholds = false,
    withFooter = false,
    defaultDecimalPlaces = 2,
    sortingFn,
    shouldRender,
    getShimmerHeight,
    isLoading,
    size,
    sectionType,
    enableSorting,
  } = options;

  return {
    id,
    enableColumnFilter: true,
    ...(size && { size }),
    ...(enableSorting !== undefined && { enableSorting }),
    minSize: COST_COLUMN_MIN_SIZE,
    maxSize: COST_COLUMN_MAX_SIZE,
    header: () => <Content id={headerContentId} />,
    accessorFn: accessor,
    cell: function Cell({
      getValue,
      row: { original },
      column: { id: columnId },
      table,
    }) {
      const value = getValue<number | null>();

      // Get user-defined precision for decimal places
      const { userDefinedPrecision } = useColumnUserSetting(
        columnId,
        table.options.meta?.sectionType ?? sectionType ?? SectionType.Sales
      );

      const loading = isLoading ? isLoading(original) : original == null;

      if (loading) {
        const shimmerHeight = getShimmerHeight
          ? getShimmerHeight(original)
          : SHIMMERING_DIV_HEIGHT_SINGLE_SALE;
        return <TableShimmeringDiv height={String(shimmerHeight)} />;
      }

      // Check if cell should be rendered
      if (shouldRender && !shouldRender(original)) {
        return null;
      }

      if (!value) {
        return <BaseCell />;
      }

      // Use DECIMAL_OPTION_MAPPINGS to handle all percent format options
      const defaultDecimalOption = {
        displayAsDecimal: false,
        roundToDigits: defaultDecimalPlaces,
      };
      const { displayAsDecimal = false, roundToDigits } = userDefinedPrecision
        ? (DECIMAL_OPTION_MAPPINGS[
            userDefinedPrecision as FormatOption_Percent
          ] ?? defaultDecimalOption)
        : defaultDecimalOption;

      const thresholds = withThresholds
        ? table.options.meta?.numericColorThresholds?.[id]
        : undefined;

      // displayAsDecimal means show as 0.15 instead of 15%
      const displayValue = displayAsDecimal
        ? value
        : value != null
          ? value * 100
          : null;
      const suffix = displayAsDecimal ? '' : '%';

      return (
        <ConditionalCell
          value={value}
          displayValue={displayValue}
          thresholds={thresholds}
          formatter={(v) => `${v.toFixed(roundToDigits)}${suffix}`}
          isLoading={false}
        />
      );
    },
    sortingFn: sortingFn ?? (sortGeneric as SortingFn<TData>),
    ...(withFooter && {
      footer: function Footer({ column: { id: columnId, accessorFn }, table }) {
        const { userDefinedPrecision } = useColumnUserSetting(
          columnId,
          table.options.meta?.sectionType ?? SectionType.Sales
        );

        const defaultDecimalOption = {
          displayAsDecimal: false,
          roundToDigits: defaultDecimalPlaces,
        };
        const { displayAsDecimal = false, roundToDigits } = userDefinedPrecision
          ? (DECIMAL_OPTION_MAPPINGS[
              userDefinedPrecision as FormatOption_Percent
            ] ?? defaultDecimalOption)
          : defaultDecimalOption;

        const original = table.options.meta?.summaryData;

        if (!original) {
          return (
            <TableShimmeringDiv height={SHIMMERING_DIV_HEIGHT_SINGLE_SALE} />
          );
        }

        const value = accessorFn?.(original, -1) as number | null;

        if (value == null) return null;

        const displayValue = displayAsDecimal ? value : value * 100;
        const suffix = displayAsDecimal ? '' : '%';

        return (
          <ConditionalCell
            displayValue={displayValue}
            value={value}
            formatter={(v) => `${v.toFixed(roundToDigits)}${suffix}`}
            isLoading={false}
          />
        );
      },
    }),
    filterFn: percentRangeFilter,
    meta: {
      columnFilterVariant: 'numberRange',
    },
  };
};
